import './CommentsBox.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import TextareaAutosize from 'react-textarea-autosize';

export interface CommentsBoxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onBlur?: () => void;
  compact?: boolean;
  onSave?: (value: string) => Promise<void> | void;
}

const CommentsBox: React.FC<CommentsBoxProps> = ({
  value,
  onChange,
  placeholder = '',
  onBlur,
  compact = false,
  onSave,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoSizeTextarea = (): void => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // Sync when external `value` changes (e.g. athlete switch). Intentionally omit
  // `localValue` from deps so typing ahead of a slow parent does not reset the field.
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
      setDirty(false);
      setJustSaved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when `value` changes
  }, [value]);

  useEffect(() => {
    autoSizeTextarea();
  }, [localValue, compact]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
    setDirty(true);
  };

  const handleBlur = (): void => {
    const maybeSave = async (): Promise<void> => {
      if (onSave && dirty && !saving) {
        try {
          setSaving(true);
          await onSave(localValue);
          setDirty(false);
          setJustSaved(true);
          setTimeout(() => {
            setJustSaved(false);
          }, 1500);
        } finally {
          setSaving(false);
        }
      }
    };
    void maybeSave();
    onBlur?.();
  };

  const handleSave = async (): Promise<void> => {
    if (!onSave) return;
    try {
      setSaving(true);
      await onSave(localValue);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => {
        setJustSaved(false);
      }, 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`comments-box ${compact ? 'comments-box-compact' : ''}`}>
      {!compact ? (
        <h3 className="comments-box-title">Comments</h3>
      ) : null}
      <div className="comments-textarea-wrap">
        <TextareaAutosize
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`comments-textarea ${compact ? 'comments-textarea-compact' : ''}`.trim()}
          minRows={2}
        />
        {!dirty && justSaved ? (
          <span className="comments-saved-indicator comments-saved-indicator--floating">Saved!</span>
        ) : null}
      </div>
      <div className="comments-actions">
        {dirty && onSave ? (
          <button
            type="button"
            className="comments-save-button"
            onClick={() => {
              void handleSave();
            }}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CommentsBox;
